import { reflect } from '@effector/reflect';
import { NumberInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { PenSquare } from 'lucide-react';
import { or } from 'patronum';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm } from 'shared/ui';
import { EditVoucherModel } from '../../model';

export const createEditVoucherModal = (model: EditVoucherModel) => {
	const ModalForm = createModalForm(model);

	const DepositField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: model.$modalData.map((voucher) => voucher?.currency ?? null),
			'data-test-id': 'edit-voucher-deposit-input',
			label: translated('edit-voucher.deposit'),
			placeholder: translated('edit-voucher.deposit-placeholder'),
			value: model.form.fields.deposit.$value,
			onChange: (value) => model.form.fields.deposit.change(String(value ?? '')),
			error: combine(
				[model.form.fields.deposit.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-voucher.deposit'),
					}),
			),
		},
	});

	const WageringField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: model.$modalData.map((voucher) => voucher?.currency ?? null),
			'data-test-id': 'edit-voucher-wagering-input',
			label: translated('edit-voucher.wagering'),
			placeholder: translated('edit-voucher.wagering-placeholder'),
			value: model.form.fields.wagering.$value,
			onChange: (value) => model.form.fields.wagering.change(String(value ?? '')),
			error: combine(
				[model.form.fields.wagering.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-voucher.wagering'),
					}),
			),
		},
	});

	const EditVoucherModalActionIcon = reflect({
		view: ModalForm.ActionIcon,
		bind: {
			'data-test-id': 'edit-voucher-action-icon',
			disabled: or(
				viewerModel.$role.map((role) => role !== USER_ROLES.god),
				model.$modalData.map((voucher) => voucher && voucher.currentCount < voucher?.totalCount),
			),
			variant: 'ghost-secondary',
			children: <PenSquare />,
		},
	});

	const EditVoucherModal = () => {
		const { t } = useTranslation();

		const [pending, pristine] = useUnit([model.$pending, model.form.$pristine]);

		return (
			<ModalForm
				title={t('edit-voucher.title')}
				cancelButtonProps={{
					children: t('edit-voucher.cancel'),
				}}
				submitButtonProps={{
					children: t('edit-voucher.save'),
					loading: pending,
					disabled: pristine,
				}}
			>
				<DepositField />
				<WageringField />
			</ModalForm>
		);
	};

	return Object.assign(EditVoucherModal, {
		ActionIcon: EditVoucherModalActionIcon,
	});
};
